// Frontend models for the Scheduling/Booking module. Booking equals the backend BookingDTO.

export enum BookingStatus {
  OPPORTUNITY = 'opportunity',
  REQUESTED = 'requested',
  AWAITING_REPLY = 'awaiting_reply',
  CONFIRMED = 'confirmed',
  CANCELLED = 'cancelled',
}

export enum MeetingMode {
  VIRTUAL = 'virtual',
  IN_PERSON = 'in_person',
}

export enum LocationKind {
  FIXED = 'fixed',
  ACCOUNT_DEFAULT = 'account_default',
  USER_DEFAULT = 'user_default',
}

export interface LocationRef {
  kind: LocationKind
  id?: string | null
}

export enum DeliveryMode {
  SEND = 'send',
  DRAFT = 'draft',
}

export enum ResourceRefType {
  ROOM = 'room',
  SESSION = 'session',
  EMAIL = 'email',
}

export interface ResourceRef {
  type: ResourceRefType
  ids: Record<string, string>
}

export interface BookingClient {
  contactId?: string
  firstName?: string
  lastName?: string
  email?: string
  timeZone?: string
  inviteUrl?: string
}

export interface BookingInvitee {
  userId?: string
  contactId?: string
  firstName?: string
  lastName?: string
  email: string
  role?: string
}

export interface ReminderInfo {
  count?: number
  lastSentAt?: string
}

export interface Booking {
  bookingId: string
  status: BookingStatus
  title?: string
  eventTypeId?: string
  userId: string
  clients: BookingClient[]
  additionalInvitees?: BookingInvitee[]
  bookedBy?: BookingClient
  source?: ResourceRef
  start?: string
  end?: string
  suggestedDate?: string
  invitationSent?: boolean
  reminders?: ReminderInfo
  rescheduledBy?: string
  originalStart?: string
  clientMessage?: string
  room?: ResourceRef
  session?: ResourceRef
  meetingMode?: MeetingMode
  location?: LocationRef | null
  meetingUrl?: string
  manageUrl?: string
  rescheduleUrl?: string
  cancelUrl?: string
  createdAt: string
  updatedAt: string
}

export enum EventTypeAccess {
  ACCOUNT = 'account',
  PERSONAL = 'personal',
}

export enum DayOfWeek {
  MONDAY = 'monday',
  TUESDAY = 'tuesday',
  WEDNESDAY = 'wednesday',
  THURSDAY = 'thursday',
  FRIDAY = 'friday',
  SATURDAY = 'saturday',
  SUNDAY = 'sunday',
}

export interface TimeInterval {
  start: string
  end: string
}

export interface DayAvailability {
  day: DayOfWeek
  intervals: TimeInterval[]
}

export interface AvailabilitySchedule {
  days: DayAvailability[]
}

export interface SchedulingSlot {
  start: string
  end: string
  userId?: string
}

export interface Location {
  id: string
  name: string
  address: string
  note?: string
  timeZone?: string
  ownerUserId?: string
  updatedAt: string
}

export enum AvailabilityMode {
  ACCOUNT_DEFAULT = 'account_default',
  CUSTOM = 'custom',
}

export enum EmailTemplatePurpose {
  INVITATION_TO_BOOK = 'invitation_to_book',
  BOOKING_REQUEST_DECLINED = 'booking_request_declined',
  INVITATION_TO_REBOOK = 'invitation_to_rebook',
  RESCHEDULE_REQUEST_DECLINED = 'reschedule_request_declined',
  CHANGE_NOTICE = 'change_notice',
  CANCELLATION_WITH_MESSAGE = 'cancellation_with_message',
  REMINDER_TO_BOOK = 'reminder_to_book',
  REQUEST_AWAITING_APPROVAL = 'request_awaiting_approval',
  NEW_BOOKING_NOTIFICATION = 'new_booking_notification',
  RESCHEDULING_NOTIFICATION = 'rescheduling_notification',
  CANCELLATION_NOTIFICATION = 'cancellation_notification',
}

export interface EmailTemplate {
  id: string
  name: string
  purpose: EmailTemplatePurpose
  subject: string
  body: string
}

export interface SchedulingEventType {
  id: string
  slug: string
  name: string
  description?: string
  durationMinutes: number
  clientFacingText?: string
  connectedMeetingTypeId?: string
  active: boolean
  access: EventTypeAccess
  addToUserPagesByDefault: boolean
  meetingMode: MeetingMode
  location?: LocationRef | null
  availabilityMode: AvailabilityMode
  availabilitySchedule?: AvailabilitySchedule
  bufferBeforeMinutes?: number
  bufferAfterMinutes?: number
  minimumNoticeMinutes?: number
  bookingWindowDays?: number
  usedByCount: number
  updatedAt: string
}

export interface SchedulingLink {
  id: string
  expiresAt?: string | null
}

export interface SchedulingUser {
  id: string
  slug?: string
  name?: string
  email?: string
  timeZone?: string
  avatarUrl?: string
  publicUrl?: string
}

export enum ApprovalMode {
  ALWAYS_REQUIRE = 'always',
  NEVER_REQUIRE = 'never',
  USERS_DECIDE = 'users_decide',
}

export enum AutomaticInvitesMode {
  OFF = 'off',
  ON = 'on',
  USERS_DECIDE = 'users_decide',
}

export interface SchedulingBranding {
  brandColor: string
}

export interface PublicBookingPage {
  accountSlug: string | null
  branding: SchedulingBranding
  showProfilePhotos: boolean
  showAccountName: boolean
  showLogo: boolean
}

export interface UserControls {
  allowManageOwnLocations: boolean
}

export interface ApprovalSettings {
  booking: ApprovalMode
  reschedule: ApprovalMode
}

export interface Automations {
  automaticInvites: AutomaticInvitesMode
}

export interface AccountDefaults {
  locationId: string | null
  availabilitySchedule: AvailabilitySchedule
}

export interface Compliance {
  disclosureText: string
}

export interface AccountSchedulingSettings {
  publicBookingPage: PublicBookingPage
  userControls: UserControls
  approval: ApprovalSettings
  automations: Automations
  defaults: AccountDefaults
  compliance: Compliance
}

export enum CalendarProvider {
  GOOGLE = 'google',
  OUTLOOK = 'outlook',
  EXCHANGE = 'exchange',
}

export enum MeetingPlatformProvider {
  ZOOM = 'zoom',
  GOOGLE_MEET = 'google_meet',
  MS_TEAMS = 'ms_teams',
}

export interface UserBookingPage {
  slug: string | null
  showProfilePhoto: boolean
}

export enum SchedulingIntegrationState {
  LIVE = 'live',
  INVALID = 'invalid',
  VALIDATING = 'validating',
}

export interface CalendarIntegration {
  provider: CalendarProvider
  connected: boolean
  state?: SchedulingIntegrationState
  capable: boolean
}

export interface MeetingPlatformIntegration {
  provider: MeetingPlatformProvider
  connected: boolean
  state?: SchedulingIntegrationState
  capable: boolean
}

export interface UserIntegrations {
  calendars: CalendarIntegration[]
  meetingPlatforms: MeetingPlatformIntegration[]
  selectedCalendar: CalendarProvider | null
  selectedMeetingPlatform: MeetingPlatformProvider | null
}

export interface UserDefaults {
  locationId: string | null
  availabilitySchedule: AvailabilitySchedule | null
}

export type UserApprovalMode = Exclude<ApprovalMode, ApprovalMode.USERS_DECIDE>

export type UserAutomaticInvitesMode = Exclude<AutomaticInvitesMode, AutomaticInvitesMode.USERS_DECIDE>

export interface UserApproval {
  booking: UserApprovalMode
  reschedule: UserApprovalMode
}

export interface UserAutomations {
  automaticInvites: UserAutomaticInvitesMode
}

export interface UserBookingRules {
  approval: UserApproval
  automations: UserAutomations
  publicBookingLinksEnabled: boolean
}

export interface UserSchedulingSettings {
  bookingPage: UserBookingPage
  integrations: UserIntegrations
  defaults: UserDefaults
  bookingRules: UserBookingRules
}

export type SettingsResetScope = 'availability' | 'booking_rules'

export interface SchedulingFilters {
  status?: BookingStatus
  userIds?: string[]
  contactIds?: string[]
  eventTypeIds?: string[]
  search?: string
  dateFrom?: Date
  dateTo?: Date
}
